import { supabase } from "@/integrations/supabase/client";

export interface AppSettingRecord {
  key: string;
  value: unknown;
  description: string | null;
  is_secret: boolean;
  updated_at: string;
}

export interface ExportRuntimeSettings {
  apiPageSize: number;
  chunkSizeRows: number;
  maxRows: number;
  defaultColumns: string[];
}

export interface RuntimeSettingSource {
  setting: string;
  source: "env" | "db" | "default";
  env: string;
  db: string;
  value: string;
}

const DEFAULT_EXPORT_RUNTIME_SETTINGS: ExportRuntimeSettings = {
  apiPageSize: 1000,
  chunkSizeRows: 10000,
  maxRows: 1000000,
  defaultColumns: [
    "property_snapshot_id",
    "property_id",
    "full_address",
    "suburb",
    "state",
    "postcode",
    "snapshot_timestamp",
    "listing_type",
    "sale_date",
    "sale_price",
    "asking_price",
    "price_display",
    "bedrooms",
    "bathrooms",
    "parking",
    "property_type",
    "adapter_slug",
    "job_id",
    "job_run_id",
    "queue_item_id",
    "source_page_url",
    "page_snapshot_id",
    "page_snapshot_provider",
    "page_snapshot_bucket",
    "page_snapshot_path",
    "page_snapshot_captured_at",
    "asset_id",
    "asset_type",
    "asset_source_uri",
    "asset_storage_bucket",
    "asset_storage_path",
    "asset_alt_text",
    "asset_generated_description",
  ],
};

export async function fetchAppSettings(keys?: string[]): Promise<AppSettingRecord[]> {
  let query = (supabase as any).from("app_settings").select("*").order("key", { ascending: true });
  if (keys && keys.length > 0) {
    query = query.in("key", keys);
  }
  const { data, error } = await query;
  if (error) {
    console.error("Error fetching app settings:", error);
    return [];
  }
  return (data || []) as AppSettingRecord[];
}

export async function upsertAppSetting(input: {
  key: string;
  value: unknown;
  description?: string | null;
  is_secret?: boolean;
}): Promise<AppSettingRecord | null> {
  const { data, error } = await (supabase as any)
    .from("app_settings")
    .upsert(
      {
        key: input.key,
        value: input.value,
        description: input.description ?? null,
        is_secret: input.is_secret ?? false,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    )
    .select("*")
    .single();

  if (error) {
    console.error("Error upserting app setting:", error);
    return null;
  }
  return data as AppSettingRecord;
}

function toSafeInteger(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

export async function fetchExportRuntimeSettings(): Promise<ExportRuntimeSettings> {
  const rows = await fetchAppSettings([
    "export.api_page_size",
    "export.chunk_size_rows",
    "export.max_rows",
    "export.default_columns",
  ]);
  const map = new Map(rows.map((row) => [row.key, row.value]));
  const columnsValue = map.get("export.default_columns");
  const defaultColumns = Array.isArray(columnsValue)
    ? columnsValue.filter((v): v is string => typeof v === "string")
    : DEFAULT_EXPORT_RUNTIME_SETTINGS.defaultColumns;

  return {
    apiPageSize: toSafeInteger(
      map.get("export.api_page_size"),
      DEFAULT_EXPORT_RUNTIME_SETTINGS.apiPageSize,
    ),
    chunkSizeRows: toSafeInteger(
      map.get("export.chunk_size_rows"),
      DEFAULT_EXPORT_RUNTIME_SETTINGS.chunkSizeRows,
    ),
    maxRows: toSafeInteger(map.get("export.max_rows"), DEFAULT_EXPORT_RUNTIME_SETTINGS.maxRows),
    defaultColumns,
  };
}

export async function fetchExportRuntimeResolution(): Promise<{
  effective: ExportRuntimeSettings;
  sources: RuntimeSettingSource[];
} | null> {
  const { data, error } = await supabase.functions.invoke("export-business-intel", {
    body: { action: "runtime-config" },
  });
  if (error) {
    console.error("Error fetching export runtime resolution:", error);
    return null;
  }
  if (!data?.success) return null;
  const effective = data.effective as ExportRuntimeSettings;
  const sources = (data.sources || []) as RuntimeSettingSource[];
  return { effective, sources };
}

/**
 * app_settings key read by SQL sale_completeness: when true, a sale counts as complete only
 * with at least one stored photo, so photo-less sales are refetched under "Complete missing
 * data". Seeded false (crawl policy phase 2, "photos off first").
 */
export const CRAWL_POLICY_REQUIRE_MEDIA_KEY = "crawl_policy.require_media";

export async function fetchCrawlPolicyRequireMedia(): Promise<boolean> {
  const { data, error } = await (supabase as any)
    .from("app_settings")
    .select("value")
    .eq("key", CRAWL_POLICY_REQUIRE_MEDIA_KEY)
    .maybeSingle();
  if (error) throw new Error(`Could not load ${CRAWL_POLICY_REQUIRE_MEDIA_KEY}: ${error.message}`);
  return data?.value === true;
}

export async function setCrawlPolicyRequireMedia(requireMedia: boolean): Promise<boolean> {
  const saved = await upsertAppSetting({
    key: CRAWL_POLICY_REQUIRE_MEDIA_KEY,
    value: requireMedia,
    description:
      "Crawl policy: a sale counts as complete only with at least one stored photo " +
      "(sale_completeness). Off: photos are not counted, so photo-less sales are not refetched.",
  });
  if (!saved) throw new Error("Could not save the crawl policy photo setting");
  return saved.value === true;
}

/**
 * app_settings key read by SQL claim_listing_card_page: for-sale card search pages (follow-until-sold
 * option B) allowed per Melbourne week, all portals together. 0 turns those searches off.
 */
export const LISTING_CARD_WEEKLY_PAGE_BUDGET_KEY = "listing_cards.weekly_page_budget";

export interface ListingCardBudgetStatus {
  weekStart: string;
  budget: number;
  used: number;
}

export async function fetchListingCardBudgetStatus(): Promise<ListingCardBudgetStatus> {
  const { data, error } = await (supabase as any).rpc("listing_card_budget_status");
  if (error) throw new Error(`Could not load the for-sale page budget: ${error.message}`);
  return data as ListingCardBudgetStatus;
}

export async function setListingCardWeeklyPageBudget(pages: number): Promise<number> {
  if (!Number.isInteger(pages) || pages < 0)
    throw new Error("The budget must be a whole number, 0 or more");
  const saved = await upsertAppSetting({
    key: LISTING_CARD_WEEKLY_PAGE_BUDGET_KEY,
    value: pages,
    description:
      "For-sale card searches (follow-until-sold): search pages allowed per Melbourne week, all " +
      "portals together. 6500 is about US$50 at the worst measured provider cost. 0 turns them off.",
  });
  if (!saved) throw new Error("Could not save the for-sale page budget");
  return Number(saved.value);
}
